import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import { RootContext } from "../../context/RootContext";
import { ROLE } from "../../shared/constants";
import { ROUTES } from "../../shared/routes";
import ChangePasswordModal from "./ChangePasswordModal";
import KeyIcon from "../../assets/images/icons/key.svg";
import LogOutIcon from "../../assets/images/icons/log-out.svg";
import TherapistsIcon from "../../assets/images/icons/sidebar/id-badge 1.svg";
import StarIcon from "../../assets/images/icons/sidebar/fi_star.svg";
import ChevronRightIcon from "../../assets/images/icons/sidebar/chevron-right.svg";
import { AppTooltip } from "../common/AppTooltip";
import s from "./SidebarUserMenu.module.scss";

function deriveInitials(
  displayName: string | null | undefined,
  email: string | null | undefined
): string {
  const name = (displayName ?? "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const local = (email ?? "").split("@")[0]?.trim() ?? "";
  return local.slice(0, 2).toUpperCase();
}

interface SidebarUserMenuProps {
  expanded: boolean;
}

export function SidebarUserMenu({ expanded }: SidebarUserMenuProps) {
  const auth = useContext(AuthContext);
  const root = useContext(RootContext);
  const navigate = useNavigate();
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);

  const user = auth?.user ?? null;
  const role = auth?.role ?? null;
  const isTenantSuperAdminInTherapistMode =
    auth?.isTenantSuperAdminInTherapistMode ?? false;
  const ownTenantId = auth?.ownTenantId ?? null;
  const setTherapistMode = auth?.setTherapistMode ?? (() => {});
  const logOut = auth?.logOut ?? (() => Promise.resolve());
  const showError = root?.showError ?? (() => {});

  if (!role || role === ROLE.TENANT_USER || role === ROLE.UNKNOWN) return null;
  if (!user) return null;

  // Real display name only — when empty we show the email in the name slot
  // instead (and drop the duplicate email row below).
  const displayName = (user.displayName ?? "").trim();
  const initials = deriveInitials(user.displayName, user.email);
  const isTenantSuperAdmin = role === ROLE.TENANT_SUPER_ADMIN;

  function handleLogout() {
    Promise.resolve(logOut())
      .then(() => (window.location.href = "/"))
      .catch((err) => showError(err));
  }

  return (
    <>
      <Dropdown
        drop="end"
        className={s.userMenu}
        data-testid="sidebar-user-menu"
      >
        <Dropdown.Toggle
          as="button"
          type="button"
          className={s.trigger}
          data-testid="sidebar-user-menu-trigger"
        >
          <span className={s.avatarWrap}>
            <span className={s.avatar} aria-hidden="true">
              {initials}
            </span>
          </span>
          {expanded && (
            <>
              <span className={s.nameCol}>
                {displayName && (
                  <span className={s.name} data-testid="sidebar-user-name">
                    {displayName}
                  </span>
                )}
                {user.email && (
                  <span className={s.emailRow}>
                    <AppTooltip
                      text={user.email}
                      placement="top"
                      popperConfig={{
                        modifiers: [
                          { name: "offset", options: { offset: [0, 8] } },
                        ],
                      }}
                    >
                      <span
                        className={displayName ? s.email : s.name}
                        data-testid="sidebar-user-email"
                      >
                        {user.email}
                      </span>
                    </AppTooltip>
                  </span>
                )}
              </span>
              <img
                src={ChevronRightIcon}
                alt=""
                className={s.chevron}
                width={16}
                height={16}
              />
            </>
          )}
        </Dropdown.Toggle>
        <Dropdown.Menu className={s.menu}>
          {isTenantSuperAdmin && isTenantSuperAdminInTherapistMode && (
            <Dropdown.Item
              className={s.navDropDownItem}
              onClick={() => {
                setTherapistMode(false);
                if (ownTenantId)
                  navigate(ROUTES.TENANTS_THERAPISTS(ownTenantId));
              }}
            >
              <img src={StarIcon} className={s.dropdownIcon} alt="" />
              Switch to Account Admin
            </Dropdown.Item>
          )}
          {isTenantSuperAdmin && !isTenantSuperAdminInTherapistMode && (
            <Dropdown.Item
              className={s.navDropDownItem}
              onClick={() => {
                setTherapistMode(true);
                if (ownTenantId) navigate(ROUTES.TENANTS_CLIENTS(ownTenantId));
              }}
            >
              <img src={TherapistsIcon} className={s.dropdownIcon} alt="" />
              Switch to Therapist mode
            </Dropdown.Item>
          )}
          <Dropdown.Item
            className={s.navDropDownItem}
            onClick={() => setShowPasswordDialog(true)}
          >
            <img src={KeyIcon} className={s.dropdownIcon} alt="" />
            Change Password
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item className={s.navDropDownItem} onClick={handleLogout}>
            <img src={LogOutIcon} className={s.dropdownIcon} alt="" />
            Log out
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      <ChangePasswordModal
        showPasswordDialog={showPasswordDialog}
        setShowPasswordDialog={setShowPasswordDialog}
      />
    </>
  );
}
